import { useEffect, useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { Alert, AlertDescription } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { SecretInput } from '@reflex/ui/components/forms/SecretInput';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { cn } from '@reflex/ui/lib/utils';
import { AlertCircle } from 'lucide-react';
import { renderInlineLinks } from '@/lib/inline-links';

interface SecretInputDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secretKey: string;
  label: string;
  description?: string;
  validationHint?: string;
  docsUrl?: string;
  isUpdate?: boolean;
  /** When the dialog opens, seed the input (e.g. wizard prefill from server env). */
  initialValue?: string | null;
  /** Short notice below the field (e.g. prefill provenance). */
  inlineHint?: string;
  /** Optional info block under the header (e.g. connected Runloop account). */
  infoSlot?: ReactNode;
  /** Inline validation error from the last save attempt (dialog stays open). */
  errorMessage?: string | null;
  /**
   * When false, the field is a plaintext input (ids, display names).
   * Defaults to true (masked secret).
   */
  sensitive?: boolean;
  onSubmit: (value: string) => void;
  isPending?: boolean;
}

export function SecretInputDialog({
  open,
  onOpenChange,
  secretKey,
  label,
  description,
  validationHint,
  docsUrl,
  isUpdate,
  initialValue,
  inlineHint,
  infoSlot,
  errorMessage,
  sensitive = true,
  onSubmit,
  isPending,
}: SecretInputDialogProps) {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initialValue != null && initialValue !== '') {
      queueMicrotask(() => {
        setValue(initialValue);
      });
    }
  }, [open, initialValue]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim() || isPending) return;
    onSubmit(value.trim());
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setValue('');
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isUpdate ? 'Update' : 'Set'} {label}
            </DialogTitle>
            <DialogDescription>
              {description ? renderInlineLinks(description) : `Enter the value for ${secretKey}.`}
              {docsUrl && (
                <>
                  {' '}
                  <a
                    href={docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2"
                  >
                    Get key
                  </a>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {infoSlot ? <div className="mt-3">{infoSlot}</div> : null}

          <div className="mt-4 space-y-2">
            <Label htmlFor="secret-value">{secretKey}</Label>
            {sensitive ? (
              <SecretInput
                id="secret-value"
                placeholder={validationHint ?? 'Enter secret value…'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-invalid={errorMessage ? true : undefined}
                className={cn(
                  errorMessage && 'border-destructive focus-visible:ring-destructive/30',
                )}
              />
            ) : (
              <Input
                id="secret-value"
                type="text"
                placeholder={validationHint ?? 'Enter value…'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-invalid={errorMessage ? true : undefined}
                className={cn(
                  errorMessage && 'border-destructive focus-visible:ring-destructive/30',
                )}
              />
            )}
            {validationHint && <p className="text-xs text-muted-foreground">{validationHint}</p>}
            {inlineHint && <p className="text-xs text-muted-foreground">{inlineHint}</p>}
          </div>

          {errorMessage ? (
            <Alert variant="destructive" className="mt-4" data-testid="secret-input-error">
              <AlertCircle className="size-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!value.trim() || isPending}>
              {isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
